/**
 * Automatically adjusts the back-to-top button color based on the background
 * of the last section in the main content area.
 */

class BackToTopColorAdapter {
  constructor() {
    this.backToTopButton = document.querySelector('.footer__back-to-top');
    this.lastSection = null;
    this.observer = null;
    
    if (!this.backToTopButton) return;
    
    this.init();
  }

  init() {
    // Initial color adjustment
    this.adjustColor();
    
    // Watch for DOM changes (sections might be loaded dynamically)
    this.observeDOMChanges();
    
    // Re-adjust on window resize (responsive design might change backgrounds)
    window.addEventListener('resize', this.debounce(() => this.adjustColor(), 250));
  }

  /**
   * Finds the last section in the main content area
   */
  findLastSection() {
    const main = document.querySelector('main');
    if (!main) return null;

    // Get all direct children of main that are sections or have the 'section' class
    const sections = Array.from(main.children).filter(child => {
      return child.tagName === 'SECTION' || 
             child.classList.contains('section') ||
             child.hasAttribute('id');
    });

    // Find the last visible section (iterate backwards)
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      const computed = window.getComputedStyle(section);
      
      // Check if section is visible (not display: none and not hidden)
      if (computed.display !== 'none' && computed.visibility !== 'hidden' && computed.opacity !== '0') {
        return section;
      }
    }

    return null;
  }

  /**
   * Traverses down from an element to find the first element with a color scheme class
   * that is actually visible in the DOM
   */
  findColorSchemeElement(element) {
    if (!element) {
      return null;
    }

    // Check if current element is visible and has a color scheme class
    const hasScheme = this.hasColorScheme(element);
    const isVisible = this.isElementVisible(element);
    
    if (hasScheme && isVisible) {
      return element;
    }

    // Recursively check children (breadth-first search)
    const queue = Array.from(element.children);
    
    let childIndex = 0;
    while (queue.length > 0) {
      const current = queue.shift();
      const currentHasScheme = this.hasColorScheme(current);
      const currentIsVisible = this.isElementVisible(current);
      
      if (currentHasScheme && currentIsVisible) {
        return current;
      }
      
      // Add children to queue
      queue.push(...Array.from(current.children));
      childIndex++;
    }

    return null;
  }

  /**
   * Checks if an element is actually visible in the DOM
   */
  isElementVisible(element) {
    if (!element) {
      return false;
    }
    
    const computed = window.getComputedStyle(element);
    const display = computed.display;
    const visibility = computed.visibility;
    const opacity = computed.opacity;
    
    // Check if element is hidden
    if (display === 'none') {
      return false;
    }
    
    if (visibility === 'hidden') {
      return false;
    }
    
    if (opacity === '0') {
      return false;
    }
    
    // Check if element has dimensions
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return false;
    }
    
    return true;
  }

  /**
   * Checks if an element has a color scheme class (color-scheme-*)
   */
  hasColorScheme(element) {
    if (!element || !element.classList) return false;
    
    return Array.from(element.classList).some(className => 
      className.startsWith('color-')
    );
  }

  /**
   * Gets the computed background color of an element
   */
  getBackgroundColor(element) {
    if (!element) return null;

    const computed = window.getComputedStyle(element);
    let bgColor = computed.backgroundColor;

    // If background is transparent, traverse up the DOM tree
    if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
      const parent = element.parentElement;
      if (parent && parent !== document.body) {
        return this.getBackgroundColor(parent);
      }
      // Default to white if we can't find a background
      return 'rgb(255, 255, 255)';
    }

    return bgColor;
  }

  /**
   * Calculates the relative luminance of an RGB color
   * Based on WCAG 2.0 formula
   */
  calculateLuminance(r, g, b) {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  /**
   * Parses an RGB/RGBA color string and returns an object with r, g, b values
   */
  parseRgbColor(colorString) {
    const match = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    
    if (match) {
      return {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3])
      };
    }
    
    return null;
  }

  /**
   * Determines if a color is light or dark
   */
  isLightColor(colorString) {
    const color = this.parseRgbColor(colorString);
    
    if (!color) return true; // Default to light if we can't parse

    const luminance = this.calculateLuminance(color.r, color.g, color.b);
    
    // Threshold of 0.5 - colors with luminance > 0.5 are considered light
    return luminance > 0.5;
  }

  /**
   * Adjusts the back-to-top button color based on the background
   */
  adjustColor() {
    const main = document.querySelector('main');
    if (!main) {
      return;
    }

    // Get all sections
    const sections = Array.from(main.children).filter(child => {
      return child.tagName === 'SECTION' || 
             child.classList.contains('section') ||
             child.hasAttribute('id');
    });

    // Iterate backwards through sections to find one with a visible color scheme element
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      const colorSchemeElement = this.findColorSchemeElement(section);
      
      if (colorSchemeElement) {
        this.lastSection = section;
        const backgroundColor = this.getBackgroundColor(colorSchemeElement);
        const isLight = this.isLightColor(backgroundColor);

        // Apply appropriate class based on background color
        if (isLight) {
          this.backToTopButton.classList.remove('back-to-top--light');
          this.backToTopButton.classList.add('back-to-top--dark');
        } else {
          this.backToTopButton.classList.remove('back-to-top--dark');
          this.backToTopButton.classList.add('back-to-top--light');
        }
        
        return; // Found and applied, we're done
      }
    }
  }

  /**
   * Observes DOM changes to re-adjust color when sections are added/removed
   */
  observeDOMChanges() {
    const main = document.querySelector('main');
    if (!main) return;

    this.observer = new MutationObserver(
      this.debounce(() => this.adjustColor(), 500)
    );

    this.observer.observe(main, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BackToTopColorAdapter();
  });
} else {
  new BackToTopColorAdapter();
}
